import { z } from 'zod';
import { AgentAttachmentType } from '../enums';
import { BuiltinContextAttachmentKinds } from './context';
var ISODateTimeString = z.string().datetime({ offset: true }).or(z.string().datetime());
var ContextAttachmentSourceSchemaV1 = z.object({
    app: z.string().min(1).optional(),
    url: z.string().url().optional(),
    route: z.string().min(1).optional(),
    domPath: z.string().min(1).optional(),
}).strict();
var ContextAttachmentSecuritySchemaV1 = z.object({
    containsPII: z.boolean().optional(),
    redactionsApplied: z.array(z.string().min(1)).max(50).optional(),
}).strict();
var ContextAttachmentTargetSchemaV1 = z.union([
    z.object({
        type: z.literal('text'),
        exact: z.string().min(1).max(4000),
        prefix: z.string().min(1).max(500).optional(),
        suffix: z.string().min(1).max(500).optional(),
        domPath: z.string().min(1).max(2000).optional(),
        startOffset: z.number().int().min(0).optional(),
        endOffset: z.number().int().min(0).optional(),
    }).strict(),
    z.object({
        type: z.literal('dom'),
        domPath: z.string().min(1).max(2000),
    }).strict(),
    z.object({
        type: z.literal('form'),
        formId: z.string().min(1).max(200).optional(),
        fieldName: z.string().min(1).max(200).optional(),
        domPath: z.string().min(1).max(2000).optional(),
    }).strict(),
]);
var ContextAttachmentMetadataBaseSchemaV1 = z.object({
    schemaVersion: z.literal(1),
    kind: z.string().min(1).max(100),
    capturedAt: ISODateTimeString,
    source: ContextAttachmentSourceSchemaV1.optional(),
    security: ContextAttachmentSecuritySchemaV1.optional(),
    target: ContextAttachmentTargetSchemaV1.optional(),
});
var WebSnippetContextSchemaV1 = ContextAttachmentMetadataBaseSchemaV1.extend({
    kind: z.literal('webSnippet'),
    title: z.string().min(1).max(200).optional(),
    selectedText: z.string().min(1).max(4000),
}).strict();
var FormSnapshotContextSchemaV1 = ContextAttachmentMetadataBaseSchemaV1.extend({
    kind: z.literal('formSnapshot'),
    formId: z.string().min(1).max(200).optional(),
    formName: z.string().min(1).max(200).optional(),
    fields: z.array(z.object({
        name: z.string().min(1).max(200),
        label: z.string().min(1).max(200).optional(),
        value: z.string().min(0).max(1000),
        inputType: z.enum(['text', 'textarea', 'number', 'select', 'radio', 'checkbox', 'date', 'email', 'tel']).optional(),
        options: z.array(z.object({
            value: z.string().min(0).max(500),
            label: z.string().min(0).max(500),
        }).strict()).max(200).optional(),
        domPath: z.string().min(1).max(2000).optional(),
    }).strict()).min(1).max(50),
}).strict();
var FormFieldContextSchemaV1 = ContextAttachmentMetadataBaseSchemaV1.extend({
    kind: z.literal('formField'),
    formId: z.string().min(1).max(200).optional(),
    fieldName: z.string().min(1).max(200),
    fieldLabel: z.string().min(1).max(200).optional(),
    value: z.string().min(0).max(1000),
    inputType: z.enum(['text', 'textarea', 'number', 'select', 'radio', 'checkbox', 'date', 'email', 'tel']).optional(),
    options: z.array(z.object({
        value: z.string().min(0).max(500),
        label: z.string().min(0).max(500),
    }).strict()).max(200).optional(),
    domPath: z.string().min(1).max(2000).optional(),
}).strict();
var CustomContextSchemaV1 = ContextAttachmentMetadataBaseSchemaV1.extend({
    // any non-built-in kind
    kind: z.string().min(1).max(100).refine(function (k) { return !BuiltinContextAttachmentKinds.includes(k); }, 'kind must not collide with built-in kinds'),
    title: z.string().min(1).max(200).optional(),
    summary: z.string().min(1).max(4000).optional(),
    data: z.record(z.string().min(1).max(200), z.unknown()).optional(),
}).strict().superRefine(function (value, ctx) {
    if (value.data) {
        // cheap payload budget guard; keep attachments small by default
        var serialized = JSON.stringify(value.data);
        if (serialized.length > 12000) {
            ctx.addIssue({
                code: 'custom',
                message: 'data is too large (max ~12k JSON chars)',
                path: ['data']
            });
        }
    }
});
export var ContextAttachmentMetadataSchemaV1 = z.union([
    WebSnippetContextSchemaV1,
    FormSnapshotContextSchemaV1,
    FormFieldContextSchemaV1,
    CustomContextSchemaV1,
]);
var AgentAttachmentBaseSchema = z.object({
    id: z.string().min(1),
    type: z.nativeEnum(AgentAttachmentType),
    name: z.string().min(1).max(500),
    url: z.string().url().optional(),
    mimeType: z.string().min(1).max(200).optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
}).strict();
export var AgentAttachmentSchema = z.union([
    AgentAttachmentBaseSchema.extend({
        type: z.literal(AgentAttachmentType.Context),
        // For context attachments, metadata must match the v1 schema.
        metadata: ContextAttachmentMetadataSchemaV1,
    }).strict(),
    AgentAttachmentBaseSchema.extend({
        type: z.literal(AgentAttachmentType.File),
        size: z.number().int().min(0).optional(),
        metadata: z.object({
            uploadedAt: z.string().optional(),
            s3Key: z.string().optional(),
            processingStatus: z.enum(['pending', 'processed', 'failed']).optional(),
            size: z.number().int().min(0).optional(),
            lastModified: z.number().optional(),
        }).optional(),
    }).strict(),
    AgentAttachmentBaseSchema.extend({
        type: z.literal(AgentAttachmentType.Link),
    }).strict(),
    AgentAttachmentBaseSchema.extend({
        type: z.literal(AgentAttachmentType.Artifact),
    }).strict(),
]);
export var SendMessagePayloadSchema = z.object({
    chatId: z.string().optional(),
    content: z.string().min(1),
    contextKey: z.string().min(1),
    attachments: z.array(AgentAttachmentSchema).optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
    model: z.string().optional(),
    temperature: z.number().optional(),
    userId: z.string().optional(),
    participantIds: z.array(z.string().min(1)).optional(),
    mentions: z.array(z.object({
        type: z.enum(['user', 'agent']),
        name: z.string().min(1),
        id: z.string().optional(),
    }).strict()).optional(),
}).strict();
//# sourceMappingURL=schemas.js.map